import { useCallback, useEffect, useState } from 'react'

function getCurrentLocation() {
  const location = typeof window === 'undefined' ? {} : window.location
  return {
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
  }
}

const redirect = (path) => (window.location = path)

/**
 * @type {Array<() => void>}
 */
const listeners = []

/**
 * Notifies all location listeners. Can be used if the history state has been manipulated
 * in by another module. Effectifely, all components using the 'useLocation' hook will
 * update.
 */
export function notify() {
  listeners.forEach((listener) => listener())
}

const NOOP = () => null

function useWindowLocation({ onPageChange = NOOP } = {}) {
  const [{ pathname, search, hash }, setLocation] = useState(getCurrentLocation())

  useEffect(() => {
    setLocation(getCurrentLocation())
  }, [])

  useEffect(() => {
    window.addEventListener('popstate', handleChange)
    return () => window.removeEventListener('popstate', handleChange)
  }, [])

  useEffect(() => {
    listeners.push(handleChange)
    return () => listeners.splice(listeners.indexOf(handleChange), 1)
  }, [])

  function handleChange() {
    setLocation(getCurrentLocation())
  }

  const updateHistory = useCallback(
    (args, cb) => {
      const originalPathname = window.location.pathname
      const newPathname = args[args.length - 1]

      if (newPathname !== originalPathname) {
        cb()
        window.dispatchEvent(new Event('popstate'))
        notify()
        onPageChange()
      }
    },
    [onPageChange],
  )

  const pushState = useCallback(
    (...args) => updateHistory(args, () => window.history.pushState(...args)),
    [updateHistory],
  )
  const replaceState = useCallback(
    (...args) => updateHistory(args, () => window.history.replaceState(...args)),
    [updateHistory],
  )

  return {
    pushState,
    replaceState,
    redirect,
    pathname,
    search,
    hash,
  }
}

export default useWindowLocation
